// MARK://dhk8/pos/features/floor/MoveOrderDialog
// Lane #165 — diálogo para mover una comanda de una mesa a otra.
//
// Flow:
//   1. Mostrar la mesa origen + comanda (folio + comensales + total)
//   2. Seleccionar mesa destino (chips de mesas DISPONIBLES — no ocupadas)
//   3. Seleccionar razón (chips predefinidas) + notas obligatorias si 'other'
//   4. Confirmar → POST /v1/orders/:id/move
//
// Mesero libre (decisión Gustavo) — la auditoría va al outbox automática
// con quién + cuándo + de dónde + a dónde + razón + notas.

import { useMemo, useState } from 'react'
import { Move, AlertCircle } from 'lucide-react'
import type { TableDto } from '@dockethub/ui-restaurant'
import { Button, Dialog, Input } from '@dockethub/ui'

import { moveOrder, type MoveOrderReason } from '../../lib/clients/orders.js'
import { useAuthStore } from '../../stores/auth-store.js'

type Reason = MoveOrderReason

const REASON_LABEL: Record<Reason, string> = {
  cleaning_required: 'Requiere limpieza',
  noise: 'Mucho ruido',
  climate_ac: 'Aire / clima',
  customer_preference: 'Preferencia cliente',
  wants_couch: 'Pidió sillón',
  wants_table: 'Pidió mesa',
  reservation_arrived: 'Llegó reserva',
  other: 'Otro motivo',
}

export interface MoveOrderDialogProps {
  /** Mesa origen — la que tiene la comanda actualmente. */
  fromTable: TableDto
  /** ID de la orden a mover. */
  orderId: string
  /** Folio para mostrar contexto al user. */
  orderFolio: string
  /** Total actual (opcional, sólo informativo). */
  totalCents?: number
  /** Todas las mesas del salón — para filtrar disponibles. */
  allTables: TableDto[]
  /** IDs de mesas ocupadas (con sesión activa) — para excluir del selector. */
  occupiedTableIds: Set<string>
  onClose: () => void
  onMoved: () => void
}

export function MoveOrderDialog({
  fromTable,
  orderId,
  orderFolio,
  totalCents,
  allTables,
  occupiedTableIds,
  onClose,
  onMoved,
}: MoveOrderDialogProps) {
  const user = useAuthStore((s) => s.user)
  const [destId, setDestId] = useState<string | null>(null)
  const [reason, setReason] = useState<Reason | null>(null)
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Mesas destino candidatas = todas - origen - ocupadas - fuera de servicio
  const candidates = useMemo(
    () =>
      allTables
        .filter((t) => t.id !== fromTable.id)
        .filter((t) => !occupiedTableIds.has(t.id))
        .filter((t) => t.status !== 'UNAVAILABLE')
        .sort((a, b) => a.code.localeCompare(b.code)),
    [allTables, fromTable.id, occupiedTableIds],
  )

  const requiresNotes = reason === 'other'
  const canSubmit = destId && reason && (!requiresNotes || notes.trim().length > 0) && !!user

  async function handleSubmit() {
    if (!canSubmit || !user || !destId || !reason) return
    setBusy(true); setErr(null)
    try {
      await moveOrder(orderId, {
        toTableId: destId,
        reason,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        movedBy: user.id,
      })
      onMoved()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo mover la comanda')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      title={`Mover comanda ${orderFolio}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={busy || !canSubmit}
            leading={<Move size={14} />}
          >
            {busy ? 'Moviendo…' : 'Mover'}
          </Button>
        </>
      }
    >
      {/* Contexto */}
      <div className="dhk8-mvord__ctx">
        <div className="dhk8-mvord__ctx-row">
          <span className="dhk8-mvord__ctx-label">De</span>
          <span className="dhk8-mvord__ctx-val"><strong>{fromTable.code}</strong> · {fromTable.capacity}p</span>
        </div>
        {totalCents !== undefined && (
          <div className="dhk8-mvord__ctx-row">
            <span className="dhk8-mvord__ctx-label">Total actual</span>
            <span className="dhk8-mvord__ctx-val">
              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(totalCents / 100)}
            </span>
          </div>
        )}
      </div>

      {/* Selector mesa destino */}
      <div className="dhk8-mvord__field">
        <span className="dhk8-mvord__label">Mesa destino</span>
        {candidates.length === 0 ? (
          <div className="dhk8-mvord__empty">
            <AlertCircle size={14} />
            No hay mesas disponibles. Espera a que se libere alguna o cierra otra cuenta.
          </div>
        ) : (
          <div className="dhk8-mvord__chips">
            {candidates.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`dhk8-mvord__chip${destId === t.id ? ' is-active' : ''}`}
                onClick={() => setDestId(t.id)}
              >
                <span className="dhk8-mvord__chip-code">{t.code}</span>
                <span className="dhk8-mvord__chip-cap">{t.capacity}p</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selector razón */}
      <div className="dhk8-mvord__field">
        <span className="dhk8-mvord__label">Motivo</span>
        <div className="dhk8-mvord__reasons">
          {(Object.keys(REASON_LABEL) as Reason[]).map((r) => (
            <button
              key={r}
              type="button"
              className={`dhk8-mvord__reason${reason === r ? ' is-active' : ''}`}
              onClick={() => setReason(r)}
            >
              {REASON_LABEL[r]}
            </button>
          ))}
        </div>
      </div>

      {/* Notas — obligatorias si 'other' */}
      <div className="dhk8-mvord__field">
        <span className="dhk8-mvord__label">
          Notas {requiresNotes ? '(obligatorio para "Otro motivo")' : '(opcional)'}
        </span>
        <Input
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          placeholder={requiresNotes ? 'Describe el motivo…' : 'Ej. cliente prefiere ventana'}
        />
      </div>

      {err && <div className="dhk8-mvord__err" role="alert">{err}</div>}

      <style>{styles}</style>
    </Dialog>
  )
}

const styles = `
  .dhk8-mvord__ctx { display: flex; flex-direction: column; gap: 6px; padding: 10px 12px; background: var(--pos-surface, #161C18); border: 1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius: var(--pos-radius-md, 8px); margin-bottom: 14px; }
  .dhk8-mvord__ctx-row { display: flex; justify-content: space-between; font-size: 12px; }
  .dhk8-mvord__ctx-label { color: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-mvord__ctx-val { color: var(--pos-fg, #E8EFE9); font-family: var(--font-mono, ui-monospace); }
  .dhk8-mvord__field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
  .dhk8-mvord__label { font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--pos-fg-tertiary, #7E8C82); font-family: var(--font-mono, ui-monospace); }
  .dhk8-mvord__chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .dhk8-mvord__chip {
    min-width: 60px; min-height: 56px; padding: 4px 8px;
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
    background: var(--pos-surface, #161C18);
    color: var(--pos-fg, #E8EFE9);
    border: 2px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    font: inherit; cursor: pointer;
    border-radius: var(--pos-radius-md, 8px);
    transition: background 120ms ease, border-color 120ms ease;
  }
  .dhk8-mvord__chip:hover { background: var(--pos-elevated, #1E2520); }
  .dhk8-mvord__chip.is-active {
    background: var(--pos-tint-soft, rgba(31,139,58,.15));
    border-color: var(--pos-tint, #1F8B3A);
    color: var(--pos-tint-on, #48C76C);
  }
  .dhk8-mvord__chip-code { font-family: var(--font-mono, ui-monospace); font-size: 14px; font-weight: 700; }
  .dhk8-mvord__chip-cap { font-size: 10px; color: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-mvord__chip.is-active .dhk8-mvord__chip-cap { color: inherit; opacity: 0.85; }
  .dhk8-mvord__reasons { display: flex; flex-wrap: wrap; gap: 4px; }
  .dhk8-mvord__reason {
    min-height: 36px; padding: 4px 12px;
    background: transparent; color: var(--pos-fg-secondary, #B6BFB8);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    font: inherit; font-size: 12px; cursor: pointer;
    border-radius: var(--pos-radius-full, 999px);
  }
  .dhk8-mvord__reason:hover { background: var(--pos-elevated, #1E2520); }
  .dhk8-mvord__reason.is-active { background: var(--pos-tint, #1F8B3A); color: #fff; border-color: var(--pos-tint, #1F8B3A); }
  .dhk8-mvord__empty {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 10px 12px;
    background: var(--pos-warning-bg, rgba(224,162,60,.10));
    border: 1px dashed var(--pos-warning, #E0A23C);
    color: var(--pos-warning, #E0A23C);
    font-size: 12px;
    border-radius: var(--pos-radius-md, 8px);
  }
  .dhk8-mvord__err { padding: 8px 12px; background: rgba(229,72,77,.15); color: var(--pos-danger, #E5484D); border: 1px solid var(--pos-danger, #E5484D); font-size: 12px; border-radius: var(--pos-radius-md, 8px); }
`
